import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import BlogLayout from "../../components/blog/BlogLayout";
import SEO from "../../components/SEO";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { BlogPostFrontmatter } from "../../types/mdx";

interface MdxModule {
  frontmatter: BlogPostFrontmatter;
  default: React.ComponentType;
}

interface TocHeading {
  id: string;
  text: string;
  level: number;
}

const mdxModules = import.meta.glob<MdxModule>("/src/content/blog/*.mdx", { eager: true });

// Mobile TOC Component
const MobileTOC: React.FC<{
  tocHeadings: TocHeading[];
  activeHeading: string;
  isOpen: boolean;
  onClose: () => void;
  readingProgress: number;
}> = ({ tocHeadings, activeHeading, isOpen, onClose, readingProgress }) => {
  if (!isOpen || tocHeadings.length === 0) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden" onClick={onClose} />

      {/* Mobile TOC Panel */}
      <div className="fixed inset-x-0 top-0 bg-gradient-to-br from-zinc-800/95 to-zinc-900/95 backdrop-blur-md border-b border-zinc-700/50 z-50 lg:hidden max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <div className="w-3 h-3 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mr-3"></div>
              Table of Contents
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-zinc-700/50"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center text-xs text-zinc-400 mb-2">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              Reading Progress: {Math.round(readingProgress)}%
            </div>
            <div className="w-full bg-zinc-700/50 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-yellow-400 to-orange-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${readingProgress}%` }}
              />
            </div>
          </div>

          {/* TOC Navigation */}
          <nav className="space-y-1">
            {tocHeadings.map(({ id, text, level }) => (
              <a
                key={id}
                href={`#${id}`}
                className={`block text-sm transition-all duration-300 py-2 px-3 rounded-lg ${
                  activeHeading === id
                    ? "text-yellow-400 font-medium bg-yellow-400/10 border-l-2 border-yellow-400"
                    : "text-zinc-300 hover:text-yellow-400 hover:bg-zinc-700/30"
                }`}
                style={{ marginLeft: `${(level - 1) * 16}px` }}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
                  onClose();
                }}
              >
                {text}
              </a>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
};

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<{ Content: React.ComponentType; frontmatter: BlogPostFrontmatter } | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPostFrontmatter[]>([]);
  const [tocHeadings, setTocHeadings] = useState<TocHeading[]>([]);
  const [activeHeading, setActiveHeading] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [readingProgress, setReadingProgress] = useState(0);
  const [showMobileToc, setShowMobileToc] = useState(false);
  const articleRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery("(max-width: 1023px)");
  useEffect(() => {
    setLoading(true);
    setError(null);

    const foundPath = Object.keys(mdxModules).find((path) => {
      const module = mdxModules[path];
      return module.frontmatter?.slug === slug;
    });

    if (foundPath) {
      const foundModule = mdxModules[foundPath];
      if (foundModule && foundModule.default && foundModule.frontmatter) {
        setPost({ Content: foundModule.default, frontmatter: foundModule.frontmatter });

        // Generate related posts
        const allPosts = Object.values(mdxModules)
          .map((module) => module.frontmatter)
          .filter((frontmatter) => frontmatter && frontmatter.slug !== slug)
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        const currentPost = foundModule.frontmatter;
        const related = allPosts
          .filter((p) => {
            // Find posts with common tags
            if (currentPost.tags && p.tags) {
              return currentPost.tags.some((tag) => p.tags?.includes(tag));
            }
            return false;
          })
          .slice(0, 3);

        // If we don't have enough related posts with common tags, fill with recent posts
        if (related.length < 3) {
          const remainingPosts = allPosts
            .filter((p) => !related.find((rp) => rp.slug === p.slug))
            .slice(0, 3 - related.length);
          related.push(...remainingPosts);
        }

        setRelatedPosts(related);
      } else {
        setError(`Post with slug "${slug}" found, but module content or frontmatter is missing.`);
      }
    } else {
      setError(`Blog post with slug "${slug}" not found.`);
    }
    setLoading(false);
  }, [slug]);

  // Extract table of contents from DOM after content is rendered
  useEffect(() => {
    if (post) {
      const timer = setTimeout(() => {
        const headings = document.querySelectorAll("article h1, article h2");
        const tocItems: TocHeading[] = Array.from(headings).map((heading) => {
          const level = parseInt(heading.tagName.charAt(1));
          const text = heading.textContent || "";
          const id =
            heading.id ||
            text
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/(^-|-$)/g, "");

          // Set ID if it doesn't exist
          if (!heading.id) {
            heading.id = id;
          }

          return { id, text, level };
        });
        setTocHeadings(tocItems);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [post]);

  // Handle scroll spy for active heading and reading progress
  useEffect(() => {
    if (!tocHeadings.length || !articleRef.current) return;

    const headingElements = tocHeadings.map(({ id }) => document.getElementById(id)).filter(Boolean);
    let readingProgressObserver;
    let headingObserver;

    // Setup reading progress observer
    const setupReadingProgress = () => {
      if (!articleRef.current) return;

      const article = articleRef.current;
      const articleSections = [];

      // Create small sentinel elements throughout the article to track progress
      const totalSentinels = 20;
      const sentinelHeight = article.scrollHeight / totalSentinels;

      for (let i = 0; i < totalSentinels; i++) {
        const sentinel = document.createElement("div");
        sentinel.style.position = "absolute";
        sentinel.style.height = "1px";
        sentinel.style.width = "1px";
        sentinel.style.top = `${(i / totalSentinels) * 100}%`;
        sentinel.style.left = "0";
        sentinel.style.pointerEvents = "none";
        sentinel.style.opacity = "0";
        sentinel.dataset.index = i.toString();
        article.style.position = article.style.position || "relative";
        article.appendChild(sentinel);
        articleSections.push(sentinel);
      }

      // Sentinel at the very end
      const endSentinel = document.createElement("div");
      endSentinel.style.position = "absolute";
      endSentinel.style.height = "1px";
      endSentinel.style.width = "1px";
      endSentinel.style.bottom = "0";
      endSentinel.style.left = "0";
      endSentinel.style.pointerEvents = "none";
      endSentinel.style.opacity = "0";
      endSentinel.dataset.index = totalSentinels.toString();
      article.appendChild(endSentinel);
      articleSections.push(endSentinel);

      // Track visible sections to calculate progress
      let visibleSections = new Set();

      readingProgressObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const index = parseInt((entry.target as HTMLElement).dataset.index!, 10);

            if (entry.isIntersecting) {
              visibleSections.add(index);
            } else {
              visibleSections.delete(index);
            }
          });

          if (visibleSections.size > 0) {
            const highestVisibleIndex = Math.max(...(Array.from(visibleSections) as number[]));
            const progress = (highestVisibleIndex / totalSentinels) * 100;
            setReadingProgress(Math.min(100, progress));
          }
        },
        {
          root: null,
          rootMargin: "0px",
          threshold: 0.1,
        }
      );

      // Observe each sentinel
      articleSections.forEach((section) => {
        readingProgressObserver.observe(section);
      });

      return () => {
        // Clean up the sentinels and observer
        articleSections.forEach((section) => {
          readingProgressObserver.unobserve(section);
          section.parentElement?.removeChild(section);
        });
      };
    };

    // Set up heading observer
    const setupHeadingObserver = () => {
      // Options for the observer
      const options = {
        root: null, // viewport
        rootMargin: "-120px 0px -80% 0px", // Adjust based on your header height
        threshold: 0,
      };

      let currentActiveHeading = null;

      headingObserver = new IntersectionObserver((entries) => {
        // Get entries that are intersecting
        const visibleHeadings = entries.filter((entry) => entry.isIntersecting).map((entry) => entry.target);

        if (visibleHeadings.length > 0) {
          // Sort by their position in the DOM if multiple are visible
          const sortedHeadings = visibleHeadings.sort((a, b) => {
            return a.getBoundingClientRect().top - b.getBoundingClientRect().top;
          });

          // Use the first visible heading
          currentActiveHeading = sortedHeadings[0].id;
          setActiveHeading(currentActiveHeading);
        } else if (entries.length === 1) {
          // If we just have one entry and it's not intersecting,
          // we need to determine if we should use prev or next heading
          const entry = entries[0];
          const allHeadings = Array.from(headingElements);
          const currentIndex = allHeadings.findIndex((h) => h.id === entry.target.id);

          if (currentIndex !== -1) {
            const rect = entry.target.getBoundingClientRect();

            if (rect.top > 120) {
              // Heading is below viewport, use previous heading
              if (currentIndex > 0) {
                currentActiveHeading = allHeadings[currentIndex - 1].id;
              }
            } else {
              // Heading is above viewport, use next heading
              if (currentIndex < allHeadings.length - 1) {
                currentActiveHeading = allHeadings[currentIndex + 1].id;
              }
            }

            if (currentActiveHeading) {
              setActiveHeading(currentActiveHeading);
            }
          }
        }
      }, options);

      // Start observing each heading
      headingElements.forEach((heading) => {
        if (heading) {
          headingObserver.observe(heading);
        }
      });
    };

    // Setup both observers
    const cleanupReadingProgress = setupReadingProgress();
    setupHeadingObserver();

    // Initial calculation for a better UX - needed because IntersectionObserver
    // might not fire immediately if headings are already visible
    setTimeout(() => {
      if (headingElements.length > 0) {
        const visibleHeading = headingElements.find((heading) => {
          if (!heading) return false;
          const rect = heading.getBoundingClientRect();
          return rect.top <= 120 && rect.bottom >= 0;
        });

        if (visibleHeading) {
          setActiveHeading(visibleHeading.id);
        } else {
          // If no visible heading, use the first one
          setActiveHeading(headingElements[0].id);
        }
      }

      // Initial reading progress
      if (articleRef.current) {
        const article = articleRef.current;
        const windowHeight = window.innerHeight;
        const scrollTop = window.scrollY;
        const articleTop = article.offsetTop;
        const articleHeight = article.scrollHeight;

        if (scrollTop > articleTop) {
          const scrolled = Math.min(scrollTop + windowHeight - articleTop, articleHeight);
          const initialProgress = (scrolled / articleHeight) * 100;
          setReadingProgress(Math.max(0, Math.min(100, initialProgress)));
        }
      }
    }, 100);

    // Cleanup function
    return () => {
      if (readingProgressObserver) {
        readingProgressObserver.disconnect();
      }
      if (headingObserver) {
        headingObserver.disconnect();
      }
      if (cleanupReadingProgress) {
        cleanupReadingProgress();
      }
    };
  }, [tocHeadings]); // Only re-run if table of contents changes

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-yellow-500/30 border-t-yellow-500 rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-12 h-12 border-4 border-transparent border-r-blue-500 rounded-full animate-spin animation-delay-150"></div>
          </div>
          <div className="text-center">
            <p className="text-zinc-300 font-medium text-lg mb-2">Loading amazing content...</p>
            <p className="text-zinc-500 text-sm">Preparing your haptic music journey</p>
          </div>
        </div>
      </div>
    );
  }
  if (error || !post) {
    // Get all available posts for suggestions
    const allPosts = Object.values(mdxModules)
      .map((module) => module.frontmatter)
      .filter((frontmatter) => frontmatter)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    // Featured article suggestions with curated selections
    const featuredSuggestions = [
      {
        slug: "science-of-groove-haptics",
        title: "The Science of Groove: How Haptics Can Make You Feel the Beat",
        excerpt:
          "Delve into the neuroscience and psychology of musical groove and discover how haptic feedback can amplify our innate connection to rhythm.",
        category: "Neuroscience",
        icon: "🧠",
        readTime: "8 min read",
      },
      {
        slug: "emotional-resonance-audio-haptics",
        title: "Emotional Resonance: The Psychological Impact of Synchronized Audio-Haptic Experiences",
        excerpt:
          "Explore how the carefully timed interplay of sound and touch can significantly amplify emotional responses to music.",
        category: "Psychology",
        icon: "💝",
        readTime: "10 min read",
      },
      {
        slug: "haptics-accessible-music-tech",
        title: "Haptics in Accessible Music Tech: Opening New Doors for All Users",
        excerpt:
          "Discover how haptic feedback can make music experiences more accessible and inclusive for individuals with disabilities.",
        category: "Accessibility",
        icon: "🤝",
        readTime: "7 min read",
      },
    ];

    // Get recent posts for additional suggestions
    const recentPosts = allPosts
      .slice(0, 6)
      .filter((post) => !featuredSuggestions.some((featured) => featured.slug === post.slug));

    return (
      <div className="min-h-screen">
        <div className="px-3 py-16 max-w-6xl mx-auto">
          {/* Breadcrumbs */}
          <nav className="mb-8">
            <ol className="flex items-center justify-center space-x-2 text-sm text-zinc-400">
              <li>
                <Link to="/" className="hover:text-yellow-400 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </li>
              <li>
                <Link to="/blog" className="hover:text-yellow-400 transition-colors">
                  Blog
                </Link>
              </li>
              <li>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </li>
              <li className="text-zinc-500">Not Found</li>
            </ol>
          </nav>

          {/* Error Message Section */}
          <div className="text-center mb-16">
            <div className="bg-zinc-800/40 backdrop-blur-sm border border-zinc-700/50 rounded-2xl p-12 max-w-2xl mx-auto">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              </div>
              <h1 className="text-3xl font-bold mb-4 text-white">Article Not Found</h1>
              <p className="text-zinc-400 mb-8 text-lg">
                {error || "The article you're looking for doesn't exist or has been moved."}
              </p>
              <Link
                to="/blog"
                className="inline-flex items-center space-x-2 bg-yellow-600 hover:bg-yellow-500 text-black font-medium px-6 py-3 rounded-lg transition-colors duration-300"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Back to Blog</span>
              </Link>
            </div>
          </div>

          {/* Featured Reading Suggestions */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4 flex items-center justify-center">
                <span className="text-2xl mr-3">✨</span>
                Featured Articles You Might Enjoy
              </h2>
              <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
                Discover the fascinating world of haptic music technology through our most popular and insightful
                articles
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {featuredSuggestions.map((article) => (
                <Link
                  key={article.slug}
                  to={`/blog/${article.slug}`}
                  className="group block bg-gradient-to-br from-zinc-800/60 to-zinc-900/60 backdrop-blur-sm border border-zinc-700/50 rounded-2xl p-8 transition-all duration-300 hover:scale-105 hover:border-yellow-400/30 hover:shadow-xl hover:shadow-yellow-400/10"
                >
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl">{article.icon}</span>
                      <span className="text-xs text-zinc-500 bg-zinc-700/50 px-3 py-1 rounded-full">
                        {article.category}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-white group-hover:text-yellow-400 transition-colors duration-300 mb-3 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-zinc-400 text-sm line-clamp-3 mb-4">{article.excerpt}</p>
                  </div>
                  <div className="flex items-center justify-between text-xs text-zinc-500">
                    <span>{article.readTime}</span>
                    <div className="flex items-center space-x-1 text-yellow-400 group-hover:text-yellow-300 transition-colors">
                      <span>Read more</span>
                      <svg
                        className="w-3 h-3 transform group-hover:tranzinc-x-1 transition-transform"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Recent Articles */}
          {recentPosts.length > 0 && (
            <div>
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-3 flex items-center justify-center">
                  <span className="text-xl mr-3">📚</span>
                  Recent Articles
                </h3>
                <p className="text-zinc-400">Explore our latest insights and discoveries</p>
              </div>

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {recentPosts.slice(0, 6).map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="group block bg-zinc-800/30 hover:bg-zinc-800/50 border border-zinc-700/30 hover:border-zinc-600/50 rounded-xl p-6 transition-all duration-300"
                  >
                    <div className="mb-4">
                      <h4 className="text-lg font-semibold text-white group-hover:text-yellow-400 transition-colors line-clamp-2 mb-2">
                        {post.title}
                      </h4>
                      {post.excerpt && <p className="text-zinc-400 text-sm line-clamp-2 mb-3">{post.excerpt}</p>}
                    </div>
                    <div className="flex items-center justify-between text-xs text-zinc-500">
                      <time>
                        {new Date(post.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </time>
                      {post.tags && post.tags.length > 0 && (
                        <span className="px-2 py-1 rounded-full border border-zinc-600/30 text-center">
                          #{post.tags[0]}
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Explore All Articles CTA */}
          <div className="text-center mt-16">
            <div className="border border-zinc-400/20 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-3">Want to explore more?</h3>
              <p className="text-zinc-400 mb-6">
                Browse our complete collection of articles on haptic music technology
              </p>
              <Link
                to="/blog"
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 text-white font-medium px-8 py-3 rounded-lg transition-all duration-300 transform hover:scale-105"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0l-4-4m4 4l-4 4" />
                </svg>
                <span>View All Articles</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags */}
      <SEO
        title={post.frontmatter.title}
        description={post.frontmatter.excerpt}
        keywords={post.frontmatter.tags}
        author={post.frontmatter.author}
        publishedTime={post.frontmatter.date}
        url={`/blog/${post.frontmatter.slug}`}
        type="article"
        frontmatter={post.frontmatter}
      />

      {/* Mobile Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-30 lg:hidden">
        <div className="w-full bg-zinc-800/80 backdrop-blur-sm h-1">
          <div
            className="bg-gradient-to-r from-yellow-400 to-orange-500 h-1 transition-all duration-300"
            style={{ width: `${readingProgress}%` }}
          />
        </div>
      </div>

      {/* Mobile TOC Trigger Button */}
      {tocHeadings.length > 0 && isMobile && (
        <button
          onClick={() => setShowMobileToc(true)}
          className="fixed bottom-20 right-6 z-30 bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-400 hover:to-orange-500 text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 lg:hidden"
          title="Table of Contents"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
        </button>
      )}

      {/* Mobile TOC */}
      <MobileTOC
        tocHeadings={tocHeadings}
        activeHeading={activeHeading}
        isOpen={showMobileToc}
        onClose={() => setShowMobileToc(false)}
        readingProgress={readingProgress}
      />
      {/* Breadcrumbs */}
      <div className="px-3 pt-8 max-w-7xl mx-auto">
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm text-zinc-400">
            <li>
              <Link to="/" className="hover:text-yellow-400 transition-colors duration-300 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                Home
              </Link>
            </li>
            <li>
              <svg className="w-4 h-4 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </li>
            <li>
              <Link to="/blog" className="hover:text-yellow-400 transition-colors duration-300 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                  />
                </svg>
                Blog
              </Link>
            </li>
            <li>
              <svg className="w-4 h-4 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </li>
            <li className="text-zinc-500 truncate max-w-xs font-medium">{post.frontmatter.title}</li>
          </ol>
        </nav>
      </div>
      <div className="px-3 flex flex-col gap-8 max-w-7xl mx-auto">
        {/* Main Content */}
        <div className="flex flex-1 justify-center gap-2 w-full">
          {" "}
          {/* Table of Contents - Desktop Sidebar */}
          {tocHeadings.length > 0 && (
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-8">
                <div className="bg-gradient-to-br from-zinc-800/60 to-zinc-900/60 backdrop-blur-sm border border-zinc-700/50 rounded-2xl p-6 shadow-xl">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <div className="w-3 h-3 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mr-3"></div>
                    Table of Contents
                  </h3>
                  <nav className="space-y-2">
                    {tocHeadings.map(({ id, text, level }) => (
                      <a
                        key={id}
                        href={`#${id}`}
                        className={`block text-sm transition-all duration-300 hover:text-yellow-400 hover:tranzinc-x-1 ${
                          activeHeading === id
                            ? "text-yellow-400 font-medium border-l-2 border-yellow-400 pl-3 bg-yellow-400/10 rounded-r-lg py-1"
                            : "text-zinc-400 hover:pl-3"
                        }`}
                        style={{ marginLeft: `${(level - 1) * 12}px` }}
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
                        }}
                      >
                        {text}
                      </a>
                    ))}
                  </nav>

                  {/* Progress Indicator */}
                  <div className="mt-6 pt-4 border-t border-zinc-700/50">
                    <div className="flex items-center text-xs text-zinc-500 mb-2">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                        />
                      </svg>
                      Reading Progress: {Math.round(readingProgress)}%
                    </div>
                    <div className="w-full bg-zinc-700/50 rounded-full h-1.5">
                      <div
                        className="bg-gradient-to-r from-yellow-400 to-orange-500 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${readingProgress}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          )}
          {/* Article Content */}
          <div ref={articleRef} className="w-full">
            <BlogLayout frontmatter={post.frontmatter}>
              <post.Content />

              {/* Related Posts Section */}
              {relatedPosts.length > 0 && (
                <div className="mt-16">
                  <div className="mb-12">
                    <div className="text-center mb-8">
                      <h2 className="text-3xl font-bold text-white mb-4 flex items-center justify-center">
                        <span className="text-2xl mr-3">🔗</span>
                        Related Articles
                      </h2>
                      <p className="text-zinc-400 text-lg">Continue your journey with these related insights</p>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                      {relatedPosts.map((relatedPost, index) => {
                        const categories = {
                          0: { icon: "🧠", category: "Neuroscience", gradient: "from-purple-500/20 to-blue-600/20" },
                          1: { icon: "💝", category: "Psychology", gradient: "from-pink-500/20 to-red-600/20" },
                          2: { icon: "🤝", category: "Technology", gradient: "from-green-500/20 to-teal-600/20" },
                        };
                        const categoryInfo = categories[index] || {
                          icon: "📚",
                          category: "Insights",
                          gradient: "from-yellow-500/20 to-orange-600/20",
                        };

                        return (
                          <Link
                            key={relatedPost.slug}
                            to={`/blog/${relatedPost.slug}`}
                            className="group block bg-gradient-to-br from-zinc-800/60 to-zinc-900/60 backdrop-blur-sm border border-zinc-700/50 rounded-2xl p-6 transition-all duration-300 hover:scale-105 hover:border-yellow-400/30 hover:shadow-xl hover:shadow-yellow-400/10"
                          >
                            <div
                              className={`absolute inset-0 bg-gradient-to-br ${categoryInfo.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`}
                            ></div>

                            <div className="relative">
                              <div className="flex items-center justify-between mb-4">
                                <span className="text-xl">{categoryInfo.icon}</span>
                                <span className="text-xs text-zinc-300 bg-zinc-700/50 px-3 py-1 rounded-full">
                                  {categoryInfo.category}
                                </span>
                              </div>

                              <h3 className="text-lg font-bold text-white group-hover:text-yellow-400 transition-colors duration-300 mb-3 line-clamp-2">
                                {relatedPost.title}
                              </h3>

                              {relatedPost.excerpt && (
                                <p className="text-zinc-400 text-sm line-clamp-3 mb-4">{relatedPost.excerpt}</p>
                              )}

                              <div className="flex items-center justify-between text-xs">
                                <time className="text-zinc-500">
                                  {new Date(relatedPost.date).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })}
                                </time>

                                <div className="flex items-center space-x-1 text-yellow-400 group-hover:text-yellow-300 transition-colors">
                                  <span className="text-xs">Read article</span>
                                  <svg
                                    className="w-3 h-3 transform group-hover:tranzinc-x-1 transition-transform"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M9 5l7 7-7 7"
                                    />
                                  </svg>
                                </div>
                              </div>

                              {relatedPost.tags && relatedPost.tags.length > 0 && (
                                <div className="flex gap-2 mt-3 justify-end">
                                  {relatedPost.tags.slice(0, 1).map((tag) => (
                                    <span
                                      key={tag}
                                      className="text-xs px-2 py-1 rounded-full border border-zinc-600/30 text-zinc-400"
                                    >
                                      #{tag}
                                    </span>
                                  ))}
                                  {relatedPost.tags.length > 2 && (
                                    <span className="text-xs text-zinc-500 px-2 py-1">
                                      +{relatedPost.tags.length - 2}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </Link>
                        );
                      })}
                    </div>{" "}
                  </div>
                </div>
              )}
            </BlogLayout>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPostPage;
